<template>
  <div ref="commentList" class="interactive-social-wall__modal-comment-list">
    <article v-for="commentEntry in flattenedComments"
             :key="commentEntry.key"
             :class="[
        getCommentDepthClass(commentEntry.depth),
        {
          'interactive-social-wall__modal-comment--reply':
            commentEntry.depth > 0,
        },
      ]"
             class="interactive-social-wall__modal-comment"
    >
      <SocialWallAvatar :name="getCommentAuthorName(commentEntry.comment)"
                        :photo="getCommentAuthorPhoto(commentEntry.comment)"
                        class="interactive-social-wall__modal-comment-avatar"
      />

      <div class="interactive-social-wall__modal-comment-content">
        <div class="interactive-social-wall__modal-comment-bubble">
          <div>
            <span class="interactive-social-wall__modal-comment-author">
              <strong>{{ getCommentAuthorName(commentEntry.comment) }}</strong>
              <small v-if="commentEntry.depth > 0">
                <i aria-hidden="true" class="uil-corner-down-right-alt"/>
                {{ $t('general.reply') }}
              </small>
            </span>
            <time :datetime="commentEntry.comment.created_at || ''">
              {{ getCommentTimeText(commentEntry.comment) }}
            </time>
          </div>
          <p>{{ getCommentText(commentEntry.comment) }}</p>
        </div>

        <div class="interactive-social-wall__modal-comment-actions">
          <span v-if="isAwaitingAdminApproval(commentEntry.comment)"
                :title="$t('general.sent_to_admin_approval')"
                class="interactive-social-wall__comment-approval-status"
                role="status"
          >
            <i aria-hidden="true" class="uil-clock-three"/>
            <span>{{ $t("general.sent_to_admin_approval") }}</span>
          </span>

          <button v-if="getReplyCount(commentEntry.comment)"
                  :aria-expanded="isRepliesExpanded(commentEntry)"
                  :disabled="busy"
                  class="interactive-social-wall__modal-comment-replies-toggle"
                  type="button"
                  @click="toggleReplies(commentEntry)"
          >
            <i :class="isRepliesExpanded(commentEntry)
                  ? 'uil-angle-up'
                  : 'uil-angle-down'"
               aria-hidden="true"
            />
            {{
              isRepliesExpanded(commentEntry)
                  ? $t('general.hide_replies')
                  : $t('general.show_replies')
            }}
          </button>

          <button v-if="getCommentId(commentEntry.comment)"
                  :aria-expanded="isReplyFormOpen(commentEntry.comment)"
                  :disabled="busy"
                  class="interactive-social-wall__modal-comment-reply-button"
                  type="button"
                  @click="toggleReplyForm(commentEntry.comment)"
          >
            <i aria-hidden="true" class="uil-comment"/>
            {{
              isReplyFormOpen(commentEntry.comment) ? $t('general.close_answer') : $t('general.answer')
            }}
          </button>

          <template v-if="canDeleteComment(commentEntry.comment)">
            <button v-if="!isDeleteConfirmationOpen(commentEntry.comment)"
                    :disabled="busy"
                    class="interactive-social-wall__modal-comment-delete-button"
                    type="button"
                    @click="openDeleteConfirmation(commentEntry.comment)"
            >
              <i aria-hidden="true" class="uil-trash-alt"/>
              {{ $t('general.delete') }}
            </button>

            <span v-else :aria-label="$t('general.comment_delete_confirmation')"
                  class="interactive-social-wall__delete-confirmation"
                  role="group">
              <small>{{ $t('general.should_it_be_deleted') }}</small>
              <button :disabled="isCommentDeleting(commentEntry.comment)"
                      type="button"
                      @click="confirmCommentDeletion(commentEntry.comment)"
              >
                <span v-if="isCommentDeleting(commentEntry.comment)"
                      aria-hidden="true"
                      class="interactive-social-wall__inline-spinner"
                />
                {{
                  isCommentDeleting(commentEntry.comment)
                      ? $t('general.deleting')
                      :  $t('general.yes')
                }}
              </button>
              <button :disabled="isCommentDeleting(commentEntry.comment)"
                      type="button"
                      @click="closeDeleteConfirmation"
              >
                {{ $t('general.no') }}
              </button>
            </span>
          </template>
        </div>

        <p v-if="getCommentDeleteError(commentEntry.comment)"
           class="interactive-social-wall__comment-delete-error"
           role="alert"
        >
          <i aria-hidden="true" class="uil-exclamation-circle"/>
          {{ getCommentDeleteError(commentEntry.comment) }}
        </p>

        <SocialWallReplyForm v-if="isReplyFormOpen(commentEntry.comment)"
                             :busy="busy"
                             :comment="commentEntry.comment"
                             :error="replyError"
                             :submitting="replySubmitting"
                             v-on="$listeners"
                             @close-reply="resetReplyForm"
        />
      </div>
    </article>
  </div>
</template>

<script>
import UserProfileAvatar from "../../../../components/common/UserProfileAvatar.vue";
import SocialWallReplyForm from "./SocialWallReplyForm.vue";
import {
  commentId as getCommentId,
  commentReplies,
  countCommentTree,
  findCommentInTree,
  flattenCommentTree,
} from "../utils/commentTree";
import {
  isAwaitingAdminApproval,
  relativeTime,
  userName,
  userPhoto,
} from "../utils/presentation";
import { isOwnedBy } from "../utils/ownership";

export default {
  name: "SocialWallCommentList",
  components: {
    SocialWallAvatar: UserProfileAvatar,
    SocialWallReplyForm,
  },
  props: {
    comments: {
      type: Array,
      default: () => [],
    },
    currentUser: {
      type: Object,
      default: () => ({}),
    },
    busy: {
      type: Boolean,
      default: false,
    },
    replySubmitting: {
      type: Boolean,
      default: false,
    },
    replyError: {
      type: String,
      default: "",
    },
    deletingCommentId: {
      type: String,
      default: "",
    },
    deleteErrors: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      replyTargetCommentId: "",
      pendingDeleteCommentId: "",
      expandedReplyKeys: {},
    };
  },
  computed: {
    allFlattenedComments() {
      return flattenCommentTree(this.comments);
    },
    flattenedComments() {
      const ancestors = [];

      return this.allFlattenedComments.filter((commentEntry) => {
        ancestors.length = commentEntry.depth;
        const isVisible = ancestors.every((ancestorEntry) =>
            Boolean(this.expandedReplyKeys[ancestorEntry.key])
        );
        ancestors[commentEntry.depth] = commentEntry;
        return isVisible;
      });
    },
  },
  watch: {
    comments() {
      if (!this.replyTargetCommentId) return;

      if (!findCommentInTree(this.comments, this.replyTargetCommentId)) {
        this.resetReplyForm(true);
        return;
      }

      const replyTargetEntry = this.allFlattenedComments.find(
          (commentEntry) =>
              getCommentId(commentEntry.comment) === this.replyTargetCommentId
      );
      if (replyTargetEntry) this.expandReplies(replyTargetEntry);
    },
    replySubmitting(isSubmitting, wasSubmitting) {
      if (wasSubmitting && !isSubmitting && !this.replyError) {
        this.resetReplyForm(true);
      }
    },
    deletingCommentId(currentId, previousId) {
      if (
          previousId &&
          !currentId &&
          !this.deleteErrors[previousId] &&
          this.pendingDeleteCommentId === previousId
      ) {
        this.pendingDeleteCommentId = "";
      }
    },
  },
  methods: {
    getCommentId,
    isAwaitingAdminApproval,
    getCommentDepthClass(depth) {
      const safeDepth = Math.min(Math.max(Number(depth) || 0, 0), 3);
      return `interactive-social-wall__modal-comment--depth-${safeDepth}`;
    },
    getCommentAuthorName(comment) {
      return userName(comment?.user);
    },
    getCommentAuthorPhoto(comment) {
      return userPhoto(comment?.user);
    },
    getCommentTimeText(comment) {
      return relativeTime(comment);
    },
    getCommentText(comment) {
      return (
          comment?.text ||
          comment?.comment ||
          comment?.content ||
          comment?.body ||
          ""
      );
    },
    getReplyCount(comment) {
      return countCommentTree(commentReplies(comment));
    },
    isRepliesExpanded(commentEntry) {
      return Boolean(this.expandedReplyKeys[commentEntry.key]);
    },
    expandReplies(commentEntry) {
      if (this.isRepliesExpanded(commentEntry)) return;
      this.expandedReplyKeys = {
        ...this.expandedReplyKeys,
        [commentEntry.key]: true,
      };
    },
    toggleReplies(commentEntry) {
      if (this.busy) return;

      if (!this.isRepliesExpanded(commentEntry)) {
        this.expandReplies(commentEntry);
        return;
      }

      const nextExpandedReplyKeys = { ...this.expandedReplyKeys };
      delete nextExpandedReplyKeys[commentEntry.key];
      this.expandedReplyKeys = nextExpandedReplyKeys;

      const hiddenReplies = commentReplies(commentEntry.comment);
      if (findCommentInTree(hiddenReplies, this.replyTargetCommentId)) {
        this.resetReplyForm();
      }
      if (findCommentInTree(hiddenReplies, this.pendingDeleteCommentId)) {
        this.pendingDeleteCommentId = "";
      }
    },
    canDeleteComment(comment) {
      return Boolean(
          getCommentId(comment) && isOwnedBy(comment, this.currentUser)
      );
    },
    isReplyFormOpen(comment) {
      return this.replyTargetCommentId === getCommentId(comment);
    },
    toggleReplyForm(comment) {
      if (this.busy) return;
      const id = getCommentId(comment);
      if (!id || this.replyTargetCommentId === id) {
        this.resetReplyForm();
        return;
      }

      this.replyTargetCommentId = id;
      this.clearReplySubmissionError();
    },
    clearReplySubmissionError() {
      if (this.replyError) this.$emit("clear-reply-error");
    },
    resetReplyForm(forceReset = false) {
      if (this.replySubmitting && !forceReset) return;
      this.replyTargetCommentId = "";
      this.clearReplySubmissionError();
    },
    openDeleteConfirmation(comment) {
      if (!this.busy && this.canDeleteComment(comment)) {
        this.pendingDeleteCommentId = getCommentId(comment);
      }
    },
    closeDeleteConfirmation() {
      if (!this.deletingCommentId) this.pendingDeleteCommentId = "";
    },
    confirmCommentDeletion(comment) {
      if (!this.busy && this.isDeleteConfirmationOpen(comment)) {
        this.$emit("delete-comment", comment);
      }
    },
    isDeleteConfirmationOpen(comment) {
      return this.pendingDeleteCommentId === getCommentId(comment);
    },
    isCommentDeleting(comment) {
      return Boolean(
          this.deletingCommentId &&
          this.deletingCommentId === getCommentId(comment)
      );
    },
    getCommentDeleteError(comment) {
      return this.deleteErrors[getCommentId(comment)] || "";
    },
    scrollToFirstComment() {
      if (this.$refs.commentList) this.$refs.commentList.scrollTop = 0;
    },
    resetInteractionState() {
      this.resetReplyForm(true);
      this.pendingDeleteCommentId = "";
      this.expandedReplyKeys = {};
    },
  },
};
</script>

<style scoped>
.interactive-social-wall__modal-comment-list,
.interactive-social-wall__modal-comment-list * {
  box-sizing: border-box;
}

.interactive-social-wall__modal-comment-list button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  color: inherit;
  font: inherit;
  line-height: 1;
  cursor: pointer;
  background: transparent;
  border: 0;
  outline: none;
  box-shadow: none;
}

.interactive-social-wall__modal-comment-list {
  max-height: min(320px, 36vh);
  padding-right: 4px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: #ccd3dd transparent;
  scrollbar-width: thin;
}

.interactive-social-wall__modal-comment {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-bottom: 10px;
}

.interactive-social-wall__modal-comment--depth-1 {
  margin-left: 24px;
}

.interactive-social-wall__modal-comment--depth-2 {
  margin-left: 48px;
}

.interactive-social-wall__modal-comment--depth-3 {
  margin-left: 72px;
}

.interactive-social-wall__modal-comment--reply::before {
  position: absolute;
  top: -3px;
  left: -14px;
  width: 10px;
  height: 18px;
  border-bottom: 1px solid #d8e2d5;
  border-left: 1px solid #d8e2d5;
  border-radius: 0 0 0 7px;
  content: "";
}

.interactive-social-wall__modal-comment-avatar {
  display: inline-flex;
  flex: 0 0 30px;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  overflow: hidden;
  color: #ffffff;
  font-size: 9px;
  font-weight: 700;
  border-radius: 50%;
}

.interactive-social-wall__modal-comment-content {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
}

.interactive-social-wall__modal-comment-bubble {
  flex: 1;
  width: 100%;
  min-width: 0;
  padding: 8px 10px;
  background: #f5f7f9;
  border-radius: 2px 8px 8px;
}

.interactive-social-wall__modal-comment--reply
.interactive-social-wall__modal-comment-avatar {
  flex-basis: 27px;
  width: 27px;
  height: 27px;
  font-size: 8px;
}

.interactive-social-wall__modal-comment--reply
.interactive-social-wall__modal-comment-bubble {
  background: #f8faf7;
  border: 1px solid #e2ebe0;
  border-radius: 7px;
}

.interactive-social-wall__modal-comment-bubble > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.interactive-social-wall__modal-comment-author {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 5px;
}

.interactive-social-wall__modal-comment-author small {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 2px;
  color: var(--iconColor);
  font-size: 7.5px;
  font-weight: 600;
  line-height: 1;
}

.interactive-social-wall__modal-comment-author small i {
  font-size: 10px;
}

.interactive-social-wall__modal-comment-bubble strong {
  overflow: hidden;
  color: #242b37;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.4;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.interactive-social-wall__modal-comment-bubble time {
  flex: 0 0 auto;
  color: #8a94a4;
  font-size: 8.5px;
}

.interactive-social-wall__modal-comment-bubble p {
  margin: 3px 0 0;
  color: #3e4755;
  font-size: 10.5px;
  line-height: 1.5;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.interactive-social-wall__modal-comment-actions {
  display: flex;
  width: 100%;
  min-height: 24px;
  align-items: center;
  flex-wrap: wrap;
  gap: 3px;
  margin-top: 3px;
}

.interactive-social-wall__comment-approval-status {
  --iconColor: #b7791f;
  display: inline-flex;
  max-width: 100%;
  min-height: 23px;
  align-items: center;
  gap: 4px;
  margin-right: auto;
  padding: 3px 7px;
  overflow: hidden;
  color: #9a6518;
  font-size: 8.5px;
  font-weight: 600;
  line-height: 1.25;
  background: #fff7e6;
  border: 1px solid #f2d49c;
  border-radius: 6px;
}

.interactive-social-wall__comment-approval-status > i {
  flex: 0 0 auto;
  font-size: 12px;
}

.interactive-social-wall__comment-approval-status > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.interactive-social-wall__modal-comment-reply-button,
.interactive-social-wall__modal-comment-replies-toggle,
.interactive-social-wall__modal-comment-delete-button {
  min-height: 24px;
  padding: 0 5px !important;
  font-size: 8.5px !important;
  font-weight: 600 !important;
  border-radius: 4px;
  transition: color 160ms ease, background 160ms ease;
}

.interactive-social-wall__modal-comment-reply-button {
  color: var(--iconColor) !important;
}

.interactive-social-wall__modal-comment-replies-toggle {
  color: #68758b !important;
}

.interactive-social-wall__modal-comment-delete-button {
  --iconColor: #b85555;
  color: #b85555 !important;
}

.interactive-social-wall__modal-comment-reply-button i,
.interactive-social-wall__modal-comment-replies-toggle i,
.interactive-social-wall__modal-comment-delete-button i {
  margin-right: 4px;
  font-size: 11px;
}

.interactive-social-wall__modal-comment-reply-button:hover,
.interactive-social-wall__modal-comment-reply-button[aria-expanded="true"] {
  color: var(--iconColor) !important;
  background: #eff8ed !important;
}

.interactive-social-wall__modal-comment-replies-toggle:hover,
.interactive-social-wall__modal-comment-replies-toggle[aria-expanded="true"] {
  color: var(--iconColor) !important;
  background: #eff8ed !important;
}

.interactive-social-wall__modal-comment-delete-button:hover {
  --iconColor: #a33f3f;
  color: #a33f3f !important;
  background: #fff0f0 !important;
}

.interactive-social-wall__modal-comment-reply-button:disabled,
.interactive-social-wall__modal-comment-replies-toggle:disabled,
.interactive-social-wall__modal-comment-delete-button:disabled {
  cursor: wait;
  opacity: 0.5;
}

.interactive-social-wall__delete-confirmation {
  display: inline-flex;
  min-height: 24px;
  align-items: center;
  gap: 4px;
  padding: 2px 3px 2px 7px;
  color: #a33f3f;
  background: #fff3f3;
  border: 1px solid #f0cece;
  border-radius: 5px;
}

.interactive-social-wall__delete-confirmation small {
  font-size: 8px;
  font-weight: 600;
  line-height: 1;
}

.interactive-social-wall__delete-confirmation button {
  min-height: 20px;
  padding: 0 6px !important;
  color: #934141 !important;
  font-size: 7.5px !important;
  font-weight: 700 !important;
  background: #ffffff !important;
  border: 1px solid #e8caca !important;
  border-radius: 4px;
}

.interactive-social-wall__delete-confirmation button:first-of-type {
  color: #ffffff !important;
  background: #bd4d4d !important;
  border-color: #bd4d4d !important;
}

.interactive-social-wall__delete-confirmation button:disabled {
  cursor: wait;
  opacity: 0.58;
}

.interactive-social-wall__comment-delete-error {
  --iconColor: #d84f4f;
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin: 2px 0 0;
  color: #c74444;
  font-size: 8px;
  line-height: 1.4;
}

.interactive-social-wall__comment-delete-error i {
  flex: 0 0 auto;
  margin-top: 1px;
  font-size: 11px;
}

.interactive-social-wall__inline-spinner {
  display: inline-block;
  width: 11px;
  height: 11px;
  border: 1.5px solid rgba(104, 117, 139, 0.32);
  border-top-color: #50b83e;
  border-radius: 50%;
  animation: interactive-social-wall-spin 700ms linear infinite;
}

@keyframes interactive-social-wall-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .interactive-social-wall__modal-comment-list {
    max-height: none;
    overflow-y: visible;
  }
}

@media (max-width: 480px) {
  .interactive-social-wall__modal-comment--depth-1 {
    margin-left: 12px;
  }

  .interactive-social-wall__modal-comment--depth-2 {
    margin-left: 24px;
  }

  .interactive-social-wall__modal-comment--depth-3 {
    margin-left: 36px;
  }

  .interactive-social-wall__modal-comment--reply::before {
    left: -9px;
    width: 7px;
  }

  .interactive-social-wall__modal-comment-reply-button,
  .interactive-social-wall__modal-comment-delete-button {
    min-height: 30px;
    padding-right: 7px !important;
    padding-left: 7px !important;
  }

  .interactive-social-wall__delete-confirmation {
    min-height: 32px;
  }

  .interactive-social-wall__delete-confirmation button {
    min-height: 26px;
    padding-right: 8px !important;
    padding-left: 8px !important;
  }
}
</style>
